import { useCallback, useEffect, useId, useReducer, useRef, useSyncExternalStore } from "react";
import { mutationStats } from "@/lib/api";

const regs = new Map();
const listeners = new Set();
let state = { dirty: false, saving: false };
const emit = () => {
  state = { dirty: [...regs.values()].some((r) => r.dirty), saving: state.saving };
  listeners.forEach((l) => l());
};
const setSaving = (saving) => { state = { ...state, saving }; listeners.forEach((l) => l()); };

let lastUserInput = 0;
if (typeof document !== "undefined") {
  const mark = () => { lastUserInput = Date.now(); };
  ["pointerdown", "keydown", "input", "change"].forEach((ev) => document.addEventListener(ev, mark, true));
}

export const LEAVE_MSG = "You have unsaved changes. Leave this page and discard them?";
export const confirmDiscard = () => !state.dirty || window.confirm(LEAVE_MSG);
export const unsavedStore = { get: () => state };

export const unsavedActions = {
  saveAll: async () => {
    for (const r of [...regs.values()].filter((x) => x.dirty)) await r.save();
  },
  resetAll: () => [...regs.values()].filter((x) => x.dirty).forEach((r) => r.reset()),
};

export function useUnsavedState() {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);
}

const snap = (v) => JSON.stringify(v ?? null);
const tick = () => new Promise((r) => setTimeout(r, 0));

export function useUnsaved(value, restore, save, ready = value != null) {
  const id = useId();
  const [, rerender] = useReducer((x) => x + 1, 0);
  const base = useRef(null);
  const loadedAt = useRef(0);
  const latest = useRef({ value, restore, save });
  latest.current = { value, restore, save };
  const cur = ready ? snap(value) : null;

  if (!ready) base.current = null;
  else if (base.current === null) { base.current = cur; loadedAt.current = Date.now(); }
  else if (cur !== base.current && Date.now() - loadedAt.current < 800 && lastUserInput < loadedAt.current) base.current = cur;
  const dirty = ready && cur !== base.current;

  const wrapped = useCallback(async (...args) => {
    const before = { ...mutationStats };
    const sent = snap(latest.current.value);
    const startedAt = Date.now();
    setSaving(true);
    try {
      await latest.current.save(...args);
      if (mutationStats.ok > before.ok && mutationStats.fail === before.fail) {
        await tick();
        base.current = lastUserInput > startedAt ? sent : snap(latest.current.value);
      }
    } catch { /* page already reports the error */ }
    finally {
      setSaving(false);
      rerender();
    }
  }, [id]);

  const reset = useCallback(() => {
    if (base.current !== null) latest.current.restore(JSON.parse(base.current));
  }, []);

  useEffect(() => { regs.set(id, { dirty, save: wrapped, reset }); emit(); }, [id, dirty, wrapped, reset]);
  useEffect(() => () => { regs.delete(id); emit(); }, [id]);

  return wrapped;
}
